import { Component, OnInit, OnDestroy } from '@angular/core';
import { interval, Subscription } from 'rxjs';

import { ApiService } from '../../../services/api.service';

interface DeploymentInfo {
  environment: string;
  latestDeploy: {
    commitSha: string;
    shortSha: string;
    deployedAt: string;
  } | null;
  previousDeploy: {
    commitSha: string;
    shortSha: string;
    deployedAt: string;
  } | null;
  pendingCommits: CommitInfo[];
  pendingCommitsCount: number;
  shippedCommits: CommitInfo[];
  shippedCommitsCount: number;
  isUpToDate: boolean;
}

interface CommitInfo {
  sha: string;
  shortSha: string;
  message: string;
  author: string;
  commitDate: string;
  githubUrl: string;
  githubPr?: {
    number: number;
    title: string;
    url: string;
  };
}

interface DeploymentStatusResponse {
  production: DeploymentInfo;
  staging: DeploymentInfo;
  lastUpdated: string;
  errors?: {
    github?: string;
  };
}

@Component({
  selector: 'app-super-admin-deployments',
  templateUrl: './super-admin-deployments.component.html',
  styleUrls: ['./super-admin-deployments.component.scss'],
})
export class SuperAdminDeploymentsComponent implements OnInit, OnDestroy {
  public deploymentData: DeploymentStatusResponse | null = null;
  public isLoading = false;
  public error: string | null = null;
  public lastRefresh: Date | null = null;
  public canRefresh = true;

  // Track expanded state for commits
  public expandedCommits: { [key: string]: boolean } = {
    production: false,
    staging: false,
    'production-shipped': false,
    'staging-shipped': false,
  };

  private refreshSubscription?: Subscription;
  private autoRefreshSubscription?: Subscription;

  constructor(private apiService: ApiService) {}

  ngOnInit() {
    this.loadDeploymentData();
    this.startAutoRefresh();
  }

  ngOnDestroy() {
    this.refreshSubscription?.unsubscribe();
    this.autoRefreshSubscription?.unsubscribe();
  }

  async loadDeploymentData() {
    if (this.isLoading) return;

    this.isLoading = true;
    this.error = null;

    try {
      this.deploymentData = await this.apiService.getPromise<DeploymentStatusResponse>('/internal/deployments');
      this.lastRefresh = new Date();
    } catch (error: any) {
      this.error = error.message || 'Failed to load deployment data';
      console.error('Error loading deployment data:', error);
    } finally {
      this.isLoading = false;
    }
  }

  async onRefresh() {
    if (!this.canRefresh || this.isLoading) return;

    // Rate limiting: prevent refresh for 10 seconds
    this.canRefresh = false;
    setTimeout(() => {
      this.canRefresh = true;
    }, 10000);

    await this.loadDeploymentData();
  }

  private startAutoRefresh() {
    // Auto-refresh every 60 seconds
    this.autoRefreshSubscription = interval(60000).subscribe(() => {
      if (!this.isLoading) {
        this.loadDeploymentData();
      }
    });
  }

  // Removed getEnvironmentTitle - replaced with status-driven design

  formatDeployTime(deployedAt: string): string {
    const date = new Date(deployedAt);
    const options: Intl.DateTimeFormatOptions = {
      month: 'short',
      day: 'numeric',
      hour: 'numeric',
      minute: '2-digit',
      hour12: true,
    };
    return date.toLocaleString(undefined, options);
  }

  // Removed getCommitDisplayText and getCommitLinks methods since we now
  // handle links directly in the template for better user experience

  getEnvironmentStatus(env: DeploymentInfo): { color: string; urgency: string; timeAgo: string } {
    const pendingCount = env.pendingCommitsCount;
    const deployedAt = env.latestDeploy?.deployedAt;

    let color = 'success';
    let urgency = 'UP TO DATE';

    if (pendingCount === 0) {
      color = 'success';
      urgency = 'UP TO DATE';
    } else if (pendingCount <= 3) {
      color = 'warning-light';
      urgency = 'MINOR DRIFT';
    } else if (pendingCount <= 7) {
      color = 'warning';
      urgency = 'MODERATE DRIFT';
    } else {
      color = 'danger';
      urgency = 'HIGH DRIFT';
    }

    const timeAgo = deployedAt ? this.getRelativeTime(deployedAt) : '';

    return { color, urgency, timeAgo };
  }

  getRelativeTime(dateString: string): string {
    const date = new Date(dateString);
    const now = new Date();
    const diffMs = now.getTime() - date.getTime();
    const diffMins = Math.floor(diffMs / (1000 * 60));
    const diffHours = Math.floor(diffMins / 60);
    const diffDays = Math.floor(diffHours / 24);

    if (diffMins < 1) return 'just now';
    if (diffMins < 60) return `${diffMins} min${diffMins !== 1 ? 's' : ''} ago`;
    if (diffHours < 24) return `${diffHours} hour${diffHours !== 1 ? 's' : ''} ago`;
    return `${diffDays} day${diffDays !== 1 ? 's' : ''} ago`;
  }

  getCommitRelativeTime(commitDate: string): string {
    const date = new Date(commitDate);
    const now = new Date();
    const diffMs = now.getTime() - date.getTime();
    const diffMins = Math.floor(diffMs / (1000 * 60));
    const diffHours = Math.floor(diffMins / 60);
    const diffDays = Math.floor(diffHours / 24);

    if (diffMins < 1) return 'now';
    if (diffMins < 60) return `${diffMins}m`;
    if (diffHours < 24) return `${diffHours}h`;
    if (diffDays < 7) return `${diffDays}d`;
    return `${Math.floor(diffDays / 7)}w`;
  }

  formatCommitTime(commitDate: string): string {
    const date = new Date(commitDate);
    const options: Intl.DateTimeFormatOptions = {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
      hour: 'numeric',
      minute: '2-digit',
      hour12: true,
      timeZoneName: 'short',
    };
    return date.toLocaleString(undefined, options);
  }

  getGitHubCommitUrl(commitSha: string): string {
    return `https://github.com/unthread-io/unthread-web/commit/${commitSha}`;
  }

  getCommitSummary(commits: CommitInfo[]): string {
    if (commits.length === 0) return '';

    const prCount = commits.filter((c) => c.githubPr).length;
    const directCommits = commits.length - prCount;

    const parts: string[] = [];
    if (prCount > 0) parts.push(`${prCount} feature${prCount !== 1 ? 's' : ''}`);
    if (directCommits > 0) parts.push(`${directCommits} direct commit${directCommits !== 1 ? 's' : ''}`);

    return parts.join(' and ');
  }

  getVisibleCommits(commits: CommitInfo[], environment: string): { visible: CommitInfo[]; remaining: number } {
    const maxVisible = 4;
    const isExpanded = this.expandedCommits[environment];

    if (isExpanded) {
      return {
        visible: commits,
        remaining: 0,
      };
    }

    return {
      visible: commits.slice(0, maxVisible),
      remaining: Math.max(0, commits.length - maxVisible),
    };
  }

  toggleCommitsExpanded(environment: string): void {
    this.expandedCommits[environment] = !this.expandedCommits[environment];
  }

  dismissError() {
    this.error = null;
  }

  cleanPRTitle(title: string): string {
    // First, split by newlines and take only the first line (the actual title)
    const firstLine = title.split('\n')[0].trim();

    // Extract only the title portion up to the PR number in parentheses

    const prNumberMatch = firstLine.match(/\(#\d+\)/);
    if (prNumberMatch) {
      const prNumberIndex = firstLine.indexOf(prNumberMatch[0]);
      const endIndex = prNumberIndex + prNumberMatch[0].length;
      let cleanTitle = firstLine.substring(0, endIndex).trim();

      // Remove any trailing separators that might be right after the PR number
      cleanTitle = cleanTitle.replace(/\s*(- |— |-- |\s+)$/, '');

      return cleanTitle;
    }

    // Fallback: if no PR number found, truncate at common separators
    const separators = [
      ' <!-- ', // HTML comments (auto-generated descriptions)
      'https://linear', // Linear links
      '\n---', // Markdown separators
      '\n##', // Markdown headers
      '\n- ', // Bullet points at start of new line
      ' - ', // Dash separators
      ' — ', // Em dash separators
      ' -- ', // Double dash separators
    ];

    for (const separator of separators) {
      const separatorIndex = firstLine.indexOf(separator);
      if (separatorIndex !== -1) {
        return firstLine.substring(0, separatorIndex).trim();
      }
    }

    // Final fallback: return first 100 characters of the first line
    return firstLine.length > 100 ? firstLine.substring(0, 100).trim() + '...' : firstLine;
  }
}
